import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Container, Typography, Button, Paper, Grid, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, FormControl, InputLabel,
  Select, MenuItem, FormControlLabel, Switch, Alert, Avatar,
} from '@mui/material';
import {
  PlayArrow as PlayIcon, Stop as StopIcon, Add as AddIcon,
  Timer as TimerIcon, Delete as DeleteIcon,
} from '@mui/icons-material';
import { format, subDays, startOfWeek, endOfWeek, addDays, differenceInSeconds } from 'date-fns';
import { timeTrackingService } from '../services/timeTrackingService';
import { taskService } from '../services/taskService';

interface TaskOption {
  id: string;
  title: string;
  projectKey?: string;
}

const formatDuration = (minutes: number) => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const TimeTrackingPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [timeLogs, setTimeLogs] = useState<any[]>([]);
  const [activeTimer, setActiveTimer] = useState<any>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tasks, setTasks] = useState<TaskOption[]>([]);

  // Manual entry dialog
  const [entryDialog, setEntryDialog] = useState(false);
  const [entryForm, setEntryForm] = useState({
    taskId: '', description: '', duration: 60, loggedAt: format(new Date(), 'yyyy-MM-dd'),
    workType: 'Development', isBillable: true,
  });

  // Timer start dialog
  const [timerDialog, setTimerDialog] = useState(false);
  const [timerTaskId, setTimerTaskId] = useState('');
  const [timerDescription, setTimerDescription] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [logs, allTasks] = await Promise.all([
        timeTrackingService.getTimeLogs(subDays(new Date(), 30), new Date()).catch(() => []),
        taskService.getAllTasks().catch(() => []),
      ]);
      setTimeLogs(Array.isArray(logs) ? logs : []);
      const taskArray = Array.isArray(allTasks) ? allTasks : [];
      setTasks(taskArray.map((t: any) => ({
        id: t.id, title: t.title, projectKey: t.projectKey,
      })));

      // Check for active timer
      try {
        const timer = await timeTrackingService.getActiveTimer();
        setActiveTimer(timer as any);
      } catch {
        setActiveTimer(null);
      }
    } catch (err) {
      console.error('Failed to load time tracking data:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Timer tick
  useEffect(() => {
    if (!activeTimer) { setElapsedSeconds(0); return; }
    const tick = () => {
      const elapsed = differenceInSeconds(new Date(), new Date(activeTimer.startedAt));
      setElapsedSeconds(elapsed);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeTimer]);

  const handleStartTimer = async () => {
    if (!timerTaskId) return;
    try {
      const timer = await timeTrackingService.startTimer({
        taskId: timerTaskId, description: timerDescription || undefined,
      });
      setActiveTimer(timer as any);
      setTimerDialog(false);
      setTimerTaskId('');
      setTimerDescription('');
    } catch (err: any) {
      setError('Failed to start timer');
    }
  };

  const handleStopTimer = async () => {
    try {
      await timeTrackingService.stopTimer();
      setActiveTimer(null);
      loadData();
    } catch (err: any) {
      setError('Failed to stop timer');
    }
  };

  const handleManualEntry = async () => {
    if (!entryForm.taskId) return;
    try {
      await taskService.addTimeLog(entryForm.taskId, {
        description: entryForm.description || undefined,
        duration: entryForm.duration,
        loggedAt: new Date(entryForm.loggedAt),
        workType: entryForm.workType,
        isBillable: entryForm.isBillable,
      });
      setEntryDialog(false);
      loadData();
    } catch (err: any) {
      setError('Failed to log time');
    }
  };

  const handleDeleteLog = async (id: string) => {
    if (!window.confirm('Delete this time log?')) return;
    try {
      await timeTrackingService.deleteTimeLog(id);
      setTimeLogs(prev => prev.filter(l => l.id !== id));
    } catch {
      setError('Failed to delete time log');
    }
  };

  const formatElapsed = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  // Summary calculations
  const todayLogs = timeLogs.filter(l => format(new Date(l.loggedAt), 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd'));
  const weekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const weekEnd = endOfWeek(new Date(), { weekStartsOn: 1 });
  const weekLogs = timeLogs.filter(l => {
    const d = new Date(l.loggedAt);
    return d >= weekStart && d <= weekEnd;
  });
  const todayTotal = todayLogs.reduce((s, l) => s + l.duration, 0);
  const weekTotal = weekLogs.reduce((s, l) => s + l.duration, 0);
  const billableTotal = timeLogs.filter(l => l.isBillable).reduce((s, l) => s + l.duration, 0);
  const totalLogged = timeLogs.reduce((s, l) => s + l.duration, 0);

  if (loading) {
    return <Container sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Container>;
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Time Tracking</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setEntryDialog(true)}>
            Manual Entry
          </Button>
          {activeTimer ? (
            <Button variant="contained" color="error" startIcon={<StopIcon />} onClick={handleStopTimer}>
              Stop ({formatElapsed(elapsedSeconds)})
            </Button>
          ) : (
            <Button variant="contained" startIcon={<PlayIcon />} onClick={() => setTimerDialog(true)}>
              Start Timer
            </Button>
          )}
        </Box>
      </Box>

      {/* Active Timer Banner */}
      {activeTimer && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'error.50', border: '2px solid', borderColor: 'error.main' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <TimerIcon color="error" sx={{ fontSize: 40 }} />
              <Box>
                <Typography variant="h5" fontWeight="bold" color="error">
                  {formatElapsed(elapsedSeconds)}
                </Typography>
                <Typography variant="body2">
                  {(activeTimer as any).taskTitle || 'Working on task...'}
                  {activeTimer.description && ` - ${activeTimer.description}`}
                </Typography>
              </Box>
            </Box>
            <Button variant="contained" color="error" startIcon={<StopIcon />}
              onClick={handleStopTimer} size="large">
              Stop Timer
            </Button>
          </Box>
        </Paper>
      )}

      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Today</Typography>
            <Typography variant="h4" fontWeight="bold">{formatDuration(todayTotal)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">This Week</Typography>
            <Typography variant="h4" fontWeight="bold">{formatDuration(weekTotal)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Billable (30d)</Typography>
            <Typography variant="h4" fontWeight="bold" color="success.main">{formatDuration(billableTotal)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">Total (30d)</Typography>
            <Typography variant="h4" fontWeight="bold">{formatDuration(totalLogged)}</Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Time Logs Table */}
      <Paper>
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ px: 2, pt: 1 }}>
          <Tab label="Recent Logs" />
          <Tab label="Today" />
          <Tab label="This Week" />
        </Tabs>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Task</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Duration</TableCell>
                <TableCell>Work Type</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Billable</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(tabValue === 0 ? timeLogs : tabValue === 1 ? todayLogs : weekLogs)
                .sort((a, b) => new Date(b.loggedAt).getTime() - new Date(a.loggedAt).getTime())
                .map(log => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{log.taskTitle}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.description || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={formatDuration(log.duration)} size="small" color="primary" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip label={log.workType || 'General'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>{format(new Date(log.loggedAt), 'MMM d, yyyy')}</TableCell>
                  <TableCell>
                    {log.isBillable
                      ? <Chip label="Billable" size="small" color="success" />
                      : <Chip label="Non-billable" size="small" />
                    }
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => handleDeleteLog(log.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {(tabValue === 0 ? timeLogs : tabValue === 1 ? todayLogs : weekLogs).length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No time logs found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Start Timer Dialog */}
      <Dialog open={timerDialog} onClose={() => setTimerDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start Timer</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Task</InputLabel>
            <Select value={timerTaskId} label="Task" onChange={e => setTimerTaskId(e.target.value)}>
              {tasks.map(t => (
                <MenuItem key={t.id} value={t.id}>
                  {t.projectKey && `[${t.projectKey}] `}{t.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField fullWidth label="Description (optional)" value={timerDescription}
            onChange={e => setTimerDescription(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTimerDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<PlayIcon />}
            disabled={!timerTaskId} onClick={handleStartTimer}>Start</Button>
        </DialogActions>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={entryDialog} onClose={() => setEntryDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Log Time</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Task</InputLabel>
                <Select value={entryForm.taskId} label="Task"
                  onChange={e => setEntryForm({ ...entryForm, taskId: e.target.value })}>
                  {tasks.map(t => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.projectKey && `[${t.projectKey}] `}{t.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Description" value={entryForm.description}
                onChange={e => setEntryForm({ ...entryForm, description: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Duration (minutes)" type="number" value={entryForm.duration}
                onChange={e => setEntryForm({ ...entryForm, duration: parseInt(e.target.value) || 0 })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth label="Date" type="date" value={entryForm.loggedAt}
                InputLabelProps={{ shrink: true }}
                onChange={e => setEntryForm({ ...entryForm, loggedAt: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Work Type</InputLabel>
                <Select value={entryForm.workType} label="Work Type"
                  onChange={e => setEntryForm({ ...entryForm, workType: e.target.value })}>
                  <MenuItem value="Development">Development</MenuItem>
                  <MenuItem value="Design">Design</MenuItem>
                  <MenuItem value="Testing">Testing</MenuItem>
                  <MenuItem value="Research">Research</MenuItem>
                  <MenuItem value="Meeting">Meeting</MenuItem>
                  <MenuItem value="Documentation">Documentation</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControlLabel
                control={<Switch checked={entryForm.isBillable}
                  onChange={e => setEntryForm({ ...entryForm, isBillable: e.target.checked })} />}
                label="Billable" sx={{ mt: 1 }} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntryDialog(false)}>Cancel</Button>
          <Button variant="contained" disabled={!entryForm.taskId} onClick={handleManualEntry}>Log Time</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default TimeTrackingPage;
